import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw, TriangleAlert } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useReference, useSettingTemplates } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import { Combobox } from '../components/controls'
import type { RuleSetting } from './RuleSettings'

const TABS = ['summary', 'networks', 'thresholds'] as const
type TabValue = (typeof TABS)[number]

interface NetworkTypeLink {
  networkTypeId: number
  networkType: string
  isDisabledByPlatform: boolean
}

interface ThresholdLink {
  thresholdTypeId: number
  thresholdType: string
  thresholdSubType: string
  dataType: string
  templateTypeId: number
  isDisabledByPlatform: boolean
}

interface ThresholdType {
  thresholdTypeId: number
  thresholdType: string
  thresholdSubType: string
  dataType: string
  templateTypeId: number
  isDisabledByPlatform: boolean
}

/**
 * RuleSettingDetail is one setting and the two things that decide how it
 * behaves: who it applies to ([Setting.SettingType_LK_NetworkType]) and which
 * limits it accepts ([Setting.SettingType_LK_ThresholdType]).
 *
 * Both links are retired rather than deleted — each row carries
 * IsDisabledByPlatform and LastModificationDate — and the threshold picker only
 * offers the thresholds of this setting's own template, because "% Tax" on a
 * payment method would be a rule nobody could apply.
 */
export function RuleSettingDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { templates } = useSettingTemplates()
  const { networkTypes } = useReference()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [order, setOrder] = useState('')
  const [groupId, setGroupId] = useState('')
  const [newNetwork, setNewNetwork] = useState('')
  const [newThreshold, setNewThreshold] = useState('')

  const { data: setting } = useQuery({
    queryKey: ['settings', id],
    queryFn: async (): Promise<RuleSetting> => {
      const res = await api.get(`/settings/${id}`)
      return res.data as RuleSetting
    },
  })

  const { data: networkData } = useQuery({
    queryKey: ['settings', id, 'network-types'],
    queryFn: async (): Promise<{ networkTypes: NetworkTypeLink[] }> => {
      const res = await api.get(`/settings/${id}/network-types`)
      return res.data as { networkTypes: NetworkTypeLink[] }
    },
  })

  const { data: thresholdData } = useQuery({
    queryKey: ['settings', id, 'thresholds'],
    queryFn: async (): Promise<{ thresholds: ThresholdLink[] }> => {
      const res = await api.get(`/settings/${id}/thresholds`)
      return res.data as { thresholds: ThresholdLink[] }
    },
  })

  // The picker only offers what this setting's template allows, and only what is
  // still active: a retired threshold is one the platform stopped applying, so
  // it has no business being ADDED here. The links below still name a retired
  // one, because it names what the setting already accepts.
  const { data: thresholdCatalogue } = useQuery({
    queryKey: ['threshold-types', setting?.templateTypeId],
    enabled: setting !== undefined,
    queryFn: async (): Promise<{ thresholdTypes: ThresholdType[] }> => {
      const res = await api.get('/threshold-types', {
        params: { templateTypeId: setting?.templateTypeId },
      })
      return res.data as { thresholdTypes: ThresholdType[] }
    },
  })

  // The group picker is scoped to the template too: a setting may not be filed
  // under a group of another one.
  const { data: groupData } = useQuery({
    queryKey: ['setting-groups', { templateTypeId: setting?.templateTypeId, all: true }],
    enabled: setting !== undefined,
    queryFn: async (): Promise<{ settingGroups: { settingGroupTypeId: number; settingGroupType: string }[] }> => {
      const res = await api.get('/setting-groups', {
        params: { templateTypeId: setting?.templateTypeId, includeDisabled: 'true', limit: 200 },
      })
      return res.data as { settingGroups: { settingGroupTypeId: number; settingGroupType: string }[] }
    },
  })

  useEffect(() => {
    if (!setting) return
    setName(setting.settingType)
    setOrder(String(setting.displayOrder))
    setGroupId(String(setting.settingGroupId))
  }, [setting])

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['settings'] })
    qc.invalidateQueries({ queryKey: ['setting-groups'] })
  }
  const onError = (err: unknown) => setError(messageOf(err, t('ruleSettings.errors.save')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/settings/${id}`, {
        settingType: name.trim(),
        templateTypeId: setting?.templateTypeId,
        settingGroupId: Number(groupId),
        displayOrder: Number(order || 0),
        isDisabledByPlatform: setting?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      setting?.isDisabledByPlatform
        ? api.post(`/settings/${id}/restore`)
        : api.delete(`/settings/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const linkNetwork = useMutation({
    mutationFn: (networkTypeId: string) =>
      api.put(`/settings/${id}/network-types/${networkTypeId}`),
    onSuccess: () => {
      setNewNetwork('')
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleNetwork = useMutation({
    mutationFn: (l: NetworkTypeLink) =>
      l.isDisabledByPlatform
        ? api.post(`/settings/${id}/network-types/${l.networkTypeId}/restore`)
        : api.delete(`/settings/${id}/network-types/${l.networkTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const linkThreshold = useMutation({
    mutationFn: (thresholdTypeId: string) =>
      api.put(`/settings/${id}/thresholds/${thresholdTypeId}`),
    onSuccess: () => {
      setNewThreshold('')
      setError('')
      invalidate()
    },
    onError,
  })

  const toggleThreshold = useMutation({
    mutationFn: (l: ThresholdLink) =>
      l.isDisabledByPlatform
        ? api.post(`/settings/${id}/thresholds/${l.thresholdTypeId}/restore`)
        : api.delete(`/settings/${id}/thresholds/${l.thresholdTypeId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  if (!setting) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const networks = networkData?.networkTypes ?? []
  const thresholds = thresholdData?.thresholds ?? []
  const groups = groupData?.settingGroups ?? []
  const linkedNetworks = new Set(networks.map((l) => l.networkTypeId))
  const linkedThresholds = new Set(thresholds.map((l) => l.thresholdTypeId))
  const mismatched =
    setting.groupTemplateTypeId !== 0 && setting.groupTemplateTypeId !== setting.templateTypeId
  const templateName =
    templates.find((tpl) => tpl.id === setting.templateTypeId)?.name || setting.templateType
  const formatDate = (value: string | null) => (value ? new Date(value).toLocaleString() : '—')

  return (
    <div>
      <Link
        to="/rules/settings"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('ruleSettings.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="font-mono text-2xl font-semibold">{setting.settingType}</h2>
          <p className="text-sm text-muted-foreground">
            {t('ruleSettings.columns.id')} {setting.settingTypeId} · {templateName} /{' '}
            {setting.settingGroupType || `#${setting.settingGroupId}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={setting.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {setting.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {setting.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      {mismatched && (
        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-sm">
          <TriangleAlert className="mt-0.5 size-4 shrink-0 text-amber-600" />
          <span>{t('ruleSettings.mismatchDetail')}</span>
        </div>
      )}

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('ruleSettings.tabs.summary')}</Tab>
          <Tab value="networks">
            {t('ruleSettings.tabs.networks')}
            <TabCount value={networks.length} />
          </Tab>
          <Tab value="thresholds">
            {t('ruleSettings.tabs.thresholds')}
            <TabCount value={thresholds.length} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <form
              className="flex max-w-xl flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault()
                if (name.trim() && groupId) save.mutate()
              }}
            >
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="setting-name">{t('ruleSettings.columns.name')}</Label>
                <Input
                  id="setting-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  className="font-mono"
                  autoFocus
                />
                <p className="text-xs text-muted-foreground">{t('ruleSettings.nameHint')}</p>
              </div>
              <div className="flex gap-3">
                <div className="flex flex-1 flex-col gap-1.5">
                  <Label htmlFor="setting-group">{t('ruleSettings.columns.group')}</Label>
                  <Combobox
                    value={groupId}
                    onValueChange={setGroupId}
                    items={groups.map((g) => ({
                      value: String(g.settingGroupTypeId),
                      label: g.settingGroupType,
                    }))}
                    placeholder={t('ruleSettings.pickGroup')}
                    className="w-full"
                  />
                  <p className="text-xs text-muted-foreground">{t('ruleSettings.groupHint')}</p>
                </div>
                <div className="flex w-28 flex-col gap-1.5">
                  <Label htmlFor="setting-order">{t('ruleSettings.columns.order')}</Label>
                  <Input
                    id="setting-order"
                    value={order}
                    onChange={(e) => setOrder(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
                  />
                </div>
              </div>

              <ErrorBanner message={error} />

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEditing(false)
                    setError('')
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button type="submit" disabled={save.isPending || !name.trim() || !groupId}>
                  {t('common.save')}
                </Button>
              </div>
            </form>
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[14rem_1fr] gap-x-6 gap-y-3 text-sm">
                    <SummaryRow label={t('ruleSettings.columns.id')}>
                      <span className="font-mono text-xs">{setting.settingTypeId}</span>
                    </SummaryRow>
                    <SummaryRow label={t('ruleSettings.columns.name')}>
                      <span className="font-mono">{setting.settingType}</span>
                    </SummaryRow>
                    <SummaryRow label={t('ruleSettings.columns.template')}>{templateName}</SummaryRow>
                    <SummaryRow label={t('ruleSettings.columns.group')}>
                      {setting.settingGroupType || `#${setting.settingGroupId}`}
                    </SummaryRow>
                    <SummaryRow label={t('ruleSettings.columns.order')}>
                      <span className="font-mono text-xs">{setting.displayOrder}</span>
                    </SummaryRow>
                    <SummaryRow label={t('common.columns.created')}>
                      {formatDate(setting.creationDate)}
                    </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(setting.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- who it applies to ---- */}
        <TabsPanel value="networks">
          <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
            {t('ruleSettings.networks.intro')}
          </p>

          {canWrite && (
            <div className="mb-3 flex items-center justify-end gap-2">
              <Combobox
                value={newNetwork}
                onValueChange={setNewNetwork}
                items={networkTypes
                  .filter((n) => !linkedNetworks.has(n.id))
                  .map((n) => ({ value: String(n.id), label: n.name }))}
                placeholder={t('ruleSettings.networks.pick')}
                className="w-64"
              />
              <Button
                size="sm"
                disabled={!newNetwork || linkNetwork.isPending}
                onClick={() => linkNetwork.mutate(newNetwork)}
              >
                <Plus />
                {t('ruleSettings.networks.add')}
              </Button>
            </div>
          )}

          <LinkTable
            head={[t('ruleSettings.networks.column'), t('common.columns.status')]}
            canWrite={canWrite}
            empty={t('ruleSettings.networks.empty')}
            rows={networks.map((l) => ({
              key: l.networkTypeId,
              cells: [
                l.networkType || `#${l.networkTypeId}`,
                <StatusBadge key="s" disabled={l.isDisabledByPlatform} />,
              ],
              disabled: l.isDisabledByPlatform,
              onToggle: () => toggleNetwork.mutate(l),
              pending: toggleNetwork.isPending,
            }))}
          />
        </TabsPanel>

        {/* ---- which limits it accepts ---- */}
        <TabsPanel value="thresholds">
          <p className="mb-3 max-w-3xl text-sm text-muted-foreground">
            {t('ruleSettings.thresholds.intro')}
          </p>

          {canWrite && (
            <div className="mb-3 flex items-center justify-end gap-2">
              <Combobox
                value={newThreshold}
                onValueChange={setNewThreshold}
                items={(thresholdCatalogue?.thresholdTypes ?? [])
                  .filter((h) => !linkedThresholds.has(h.thresholdTypeId))
                  .map((h) => ({
                    value: String(h.thresholdTypeId),
                    label: `${h.thresholdType} (${h.dataType})`,
                  }))}
                placeholder={t('ruleSettings.thresholds.pick')}
                className="w-72"
              />
              <Button
                size="sm"
                disabled={!newThreshold || linkThreshold.isPending}
                onClick={() => linkThreshold.mutate(newThreshold)}
              >
                <Plus />
                {t('ruleSettings.thresholds.add')}
              </Button>
            </div>
          )}

          <LinkTable
            head={[
              t('ruleSettings.thresholds.column'),
              t('ruleSettings.thresholds.dataType'),
              t('common.columns.status'),
            ]}
            canWrite={canWrite}
            empty={t('ruleSettings.thresholds.empty')}
            rows={thresholds.map((l) => ({
              key: l.thresholdTypeId,
              cells: [
                l.thresholdType || `#${l.thresholdTypeId}`,
                <Badge key="d" variant="outline">
                  {l.dataType || '—'}
                </Badge>,
                <StatusBadge key="s" disabled={l.isDisabledByPlatform} />,
              ],
              disabled: l.isDisabledByPlatform,
              onToggle: () => toggleThreshold.mutate(l),
              pending: toggleThreshold.isPending,
            }))}
          />
        </TabsPanel>
      </Tabs>
    </div>
  )
}

/** LinkTable renders the two link tabs, which differ only in their columns. */
function LinkTable({
  head,
  rows,
  canWrite,
  empty,
}: {
  head: string[]
  rows: {
    key: number
    cells: React.ReactNode[]
    disabled: boolean
    onToggle: () => void
    pending: boolean
  }[]
  canWrite: boolean
  empty: string
}) {
  const { t } = useTranslation('app')

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50">
            {head.map((label, i) => (
              <TableHead key={label} className={i === 0 ? '' : 'w-40'}>
                {label}
              </TableHead>
            ))}
            {canWrite && (
              <TableHead className="w-20 text-right">{t('common.columns.actions')}</TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              {row.cells.map((cell, i) => (
                <TableCell key={i} className={i === 0 ? 'font-medium' : ''}>
                  {cell}
                </TableCell>
              ))}
              {canWrite && (
                <TableCell>
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      title={row.disabled ? t('common.restore') : t('common.retire')}
                      disabled={row.pending}
                      onClick={row.onToggle}
                    >
                      {row.disabled ? <RotateCcw /> : <Ban />}
                    </Button>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell
                colSpan={canWrite ? head.length + 1 : head.length}
                className="py-8 text-center text-muted-foreground"
              >
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
